import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { ResponsiveText } from "@ui/components/ResponsiveText";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";

// Matches the Figma "Chat with Us" pill: white rounded pill with the same layered
// shadow as `ActionPill`, a green mail glyph, and a bold prompt. Renders as a link
// to the message endpoint, which finds-or-creates the conversation and redirects
// to the thread. Distinct from the shared `MessageButton` (paperPlane / purple).
//
// `size` shrinks the pill at the midway (md–lg) breakpoint and on mobile, the same
// way the product page steps its action bar down between desktop and mobile.
const messagePill = tv({
  base: [
    "flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-white text-center",
    "font-extrabold text-neutral-900 tracking-[-0.24px]",
    "shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05),0px_3px_3px_0px_rgba(0,0,0,0.1),inset_0px_0px_0px_1px_rgba(0,0,0,0.05)]",
    "transition-colors hover:bg-neutral-25",
  ],
  variants: {
    size: {
      sm: "px-4 py-3 text-lg leading-6",
      lg: "px-5 py-4 text-2xl leading-8",
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

type MessagePillVariants = VariantProps<typeof messagePill>;

export interface MessagePillProps {
  href: string;
  size?: MessagePillVariants["size"];
  className?: string;
}

export function MessagePill({ href, size = "lg", className }: MessagePillProps) {
  return (
    <Button href={href} className={messagePill({ size, className })}>
      <Icon name="mail" size={size === "sm" ? "24" : "32"} type="sticker" />
      {/* Full prompt on mobile + wide desktop; drops to a short label through the
          narrow midway sidebar where the full copy would wrap. */}
      <ResponsiveText
        base={t("components.views.programs.show.message_cta")}
        md={t("components.views.programs.show.message_cta_short")}
        xl={t("components.views.programs.show.message_cta")}
      />
    </Button>
  );
}
